import { useState } from 'react';
import { Box, Card, CardContent, Container, Drawer, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import { MODULE_CHOOSER_PATH, useRememberLastModule } from '@core/router/moduleLanding';
import { getRouteShellOptions } from '@core/router/moduleRegistry';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { Breadcrumb } from './Breadcrumb';
import { BreadcrumbOverrideProvider } from './BreadcrumbOverrideContext';
import { Footer } from './Footer';

/**
 * Application Layout
 *
 * Responsive layout with:
 * - Fixed header with app name, language selector, user info
 * - Responsive sidebar (permanent on desktop, temporary on mobile)
 *   - Current module name / module switcher
 *   - Module-aware navigation
 * - Breadcrumb navigation
 * - Main content area with proper spacing
 * - Footer with copyright and version info
 *
 * Breakpoints:
 * - Mobile (< 900px): Temporary drawer with hamburger menu
 * - Desktop (≥ 900px): Permanent drawer
 */

const DRAWER_WIDTH = 260;
/** Width of centered pages (MUI "lg" 1200px + 20%); only large screens reach it */
const CONTENT_MAX_WIDTH = 1440;

export const AppLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Full-bleed pages (e.g. the document editor) fill the whole content area:
  // no breadcrumb, no max-width container, no padding around the card.
  // Wide pages use the full width next to the sidebar and draw their own header and card
  // (no breadcrumb, no content card around them).
  const { layout: routeLayout, breadcrumb: routeBreadcrumb } = getRouteShellOptions(location.pathname);
  const isFullBleed = routeLayout === 'fullBleed' || /\/dokumenti\/[^/]+\/uredi$/.test(location.pathname);
  const isWide = routeLayout === 'wide';
  // Wide and centered pages draw their own header and card; centered keeps the usual width
  const drawsOwnCard = isWide || routeLayout === 'centered';
  const isModuleChooser = location.pathname === MODULE_CHOOSER_PATH;

  // Remember the work module the user is in for the next login
  useRememberLastModule();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <BreadcrumbOverrideProvider>
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: 'background.default',
        // Prevent horizontal overflow at the root level
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
      }}
    >
      {/* Header - Fixed at top */}
      <Header onMenuClick={handleDrawerToggle} showMenuButton={!isModuleChooser} />

      {/* Sidebar - Responsive (not on the module chooser: no module is selected yet) */}
      {!isModuleChooser && (
        <Box
          component="nav"
          sx={{
            width: { md: DRAWER_WIDTH },
            flexShrink: { md: 0 },
          }}
        >
          {/* Mobile drawer (temporary) */}
          {isMobile ? (
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true, // Better mobile performance
              }}
              sx={{
                '& .MuiDrawer-paper': {
                  width: DRAWER_WIDTH,
                  boxSizing: 'border-box',
                  bgcolor: 'background.paper',
                  overflow: 'hidden',
                },
              }}
            >
              <Toolbar /> {/* Spacer for header */}
              <Sidebar onItemClick={handleDrawerToggle} />
            </Drawer>
          ) : (
            // Desktop drawer (permanent)
            <Drawer
              variant="permanent"
              sx={{
                '& .MuiDrawer-paper': {
                  width: DRAWER_WIDTH,
                  boxSizing: 'border-box',
                  bgcolor: 'background.paper',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                },
              }}
              open
            >
              <Toolbar /> {/* Spacer for header */}
              <Sidebar />
            </Drawer>
          )}
        </Box>
      )}

      {/* Main content area - includes content + footer */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          // Properly constrain width to prevent overflow (full width when there is no sidebar)
          width: { xs: '100%', md: isModuleChooser ? '100%' : `calc(100% - ${DRAWER_WIDTH}px)` },
          maxWidth: { xs: '100%', md: isModuleChooser ? '100%' : `calc(100% - ${DRAWER_WIDTH}px)` },
          minWidth: 0, // Allow flex item to shrink below content size
          minHeight: '100vh',
          overflowX: 'hidden',
        }}
      >
        <Toolbar /> {/* Spacer for fixed header */}

        {/* Content container - grows to push footer down */}
        <Container
          maxWidth={false}
          disableGutters={isFullBleed}
          sx={{
            maxWidth: isFullBleed || isWide ? 'none' : CONTENT_MAX_WIDTH,
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            // Allow flex children to shrink so the editor scrolls internally
            // instead of growing the whole page (full-bleed pages only).
            minHeight: isFullBleed ? 0 : undefined,
            px: isFullBleed ? 0 : { xs: 2, sm: 3 },
            py: isFullBleed ? 0 : { xs: 2, sm: 3 },
          }}
        >
          {/* Breadcrumb navigation - above the card (hidden on full-bleed and wide pages) */}
          {!isFullBleed && !isModuleChooser && routeBreadcrumb && (
            // Pages with their own card start right under the breadcrumb, so it keeps a small gap
            <Box sx={{ mb: drawsOwnCard ? 1.5 : 0 }}>
              <Breadcrumb />
            </Box>
          )}

          {/* Content Card (the module chooser and wide pages render their own cards) */}
          {isModuleChooser || drawsOwnCard ? (
            <Outlet />
          ) : (
            <Card
              elevation={1}
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: isFullBleed ? 0 : undefined,
                mt: isFullBleed ? 0 : 2,
                borderRadius: isFullBleed ? 0 : 2,
                overflow: 'hidden',
              }}
            >
              <CardContent
                sx={{
                  flexGrow: 1,
                  ...(isFullBleed
                    ? {
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        p: 0,
                        '&:last-child': { pb: 0 },
                      }
                    : {
                        p: { xs: 2, sm: 3 },
                        '&:last-child': { pb: { xs: 2, sm: 3 } },
                        // Tables and other wide content will scroll within this container
                        '& .MuiDataGrid-root, & table, & .table-container': {
                          maxWidth: '100%',
                          overflowX: 'auto',
                        },
                      }),
                }}
              >
                <Outlet />
              </CardContent>
            </Card>
          )}
        </Container>

        {/* Footer - inside main area, follows the card (hidden on full-bleed pages) */}
        {!isFullBleed && <Footer />}
      </Box>
    </Box>
    </BreadcrumbOverrideProvider>
  );
};
